/**
 * Phase 4C — Standard-plan upgrade prompt screen.
 *
 * Rendered whenever a Standard-plan tenant lands on a cost-sharing-only
 * route (Settlements/Reserves/Reconciliation), either through a direct URL
 * or a stale bookmark. Also rendered when the backend returns 402 on a
 * cost-sharing endpoint.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Users, CurrencyEur, ChartBar, CheckCircle } from "@phosphor-icons/react";
import { Surface, Btn } from "./Primitives";

const FEATURES = [
  "Co-owner cost allocation per aircraft",
  "Flight cost tracking (variable €/hr + fixed)",
  "Settlements with state machine (pending → sent → confirmed)",
  "Reserve fund buckets with target balances",
  "Year-end reconciliation report",
  "Owner usage reports (hours, contributions, fair share)",
  "Payment tracking & shared expense visibility",
];

const FEATURE_ICONS = {
  Settlements:    CurrencyEur,
  Reserves:       Users,
  Reconciliation: ChartBar,
};

export default function UpgradePrompt({ feature = "Cost sharing", description }) {
  const navigate = useNavigate();
  const Icon = FEATURE_ICONS[feature] || Lock;
  return (
    <div className="container py-12" data-testid="upgrade-prompt">
      <Surface className="p-10 lg:p-14 border-amber/30 bg-gradient-to-br from-amber/5 via-transparent to-amber/5 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-amber bg-amber/10 mb-6">
          <Icon size={24} className="text-amber" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-amber font-mono mb-3">
          Included in OwnerCAM Pro
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
          {feature} is a <span className="amber">Pro plan</span> feature.
        </h1>
        <p className="text-zinc-400 leading-relaxed max-w-xl mx-auto mb-8">
          {description || (
            <>You're currently on the Standard plan, which covers all maintenance,
            inspection, parts, AD compliance, and document tracking — but cost
            sharing is bundled into OwnerCAM Pro for partnerships, co-owners, and
            flying groups.</>
          )}
        </p>
        <ul className="text-left max-w-md mx-auto mb-10 space-y-2.5">
          {FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
              <CheckCircle size={16} weight="fill" className="text-amber mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Btn
            onClick={() => navigate("/billing?upgrade=cost_sharing")}
            data-testid="upgrade-cta-btn"
            className="bg-amber text-black hover:bg-[#FFC033]"
          >
            Upgrade to Pro <ArrowRight size={14} className="inline ml-1.5" weight="bold" />
          </Btn>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="px-5 py-2 text-sm text-zinc-400 hover:text-zinc-200"
            data-testid="upgrade-back-btn"
          >
            Back to dashboard
          </button>
        </div>
        <div className="mt-8 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
          Pro is €50/aircraft/month or €480/aircraft/year (20% off, non-refundable) · 90-day free trial
        </div>
      </Surface>
    </div>
  );
}
